// Übersicht (Screen hausmeister/01): Arbeitszeit heute, Zähler, „Jetzt dran“, Woche,
// Fahrten/Material/Abnahmen, Übergaben, „Als Nächstes“ und letzte Notizen.
// Nur Lesen – Kommen/Feierabend läuft wie bisher über den ZeitContext.
import { useCallback, useEffect, useState, type CSSProperties } from 'react';
import {
  ArrowRight, CaretRight, CarProfile, ChartBar, CircleNotch, Key, ListChecks, MoonStars, NotePencil, Package,
  PenNib, SignIn, Timer,
} from '@phosphor-icons/react';
import { GlasKennzahl, GlasWidget, IconChip, KennzahlKachel, StatusChip, farbKlasse } from '../../components/portal/bausteine';
import { useZeit } from '../../contexts/ZeitContext';
import type { HausmeisterTicket } from '../../lib/hausmeister/api';
import {
  adresse, alsNaechstes, datumLang, euro, gruss, hm, kmText, materialSumme, notizZeit, statusStufe, uhrzeit,
  wochenBalken, wochenGrenzen, zaehleStufen,
} from '../../lib/hausmeister/darstellung';
import { ladeEigenesMaterial, ladeLetzteNotizen, zaehleEigeneAbnahmen, type LetzteNotiz } from '../../lib/hausmeister/uebersicht';
import type { HausmeisterUebergabe } from '../../lib/hausmeister/uebergaben';
import { tagVon } from '../../lib/zeiterfassung/berechnung';
import { ladeFahrten, laufenderTicketTimer } from '../../lib/zeiterfassung/api';
import { zeitMinuten, type TicketZeit } from '../../lib/zeiterfassung/ticketzeit';
import { STUFE_STIL, ART_STIL } from './symbole';
import { uebergabeStatus, uebergabeTitel } from './UebergabenListe';
import { kalenderblatt } from '../../lib/hausmeister/uebergabeAnzeige';

interface Kennzahlen { km: number; materialAnzahl: number; materialSumme: number; abnahmen: number; notizen: LetzteNotiz[] }

const LEISE: CSSProperties = { position: 'relative', padding: '10px 4px', fontSize: 13.5, fontWeight: 600, color: 'var(--p-w50)' };

export default function Uebersicht({ meineId, vollerName, tickets, uebergaben, bereitetVor, onAuftrag, onAlleAuftraege, onUebergabe, onFehler }: {
  meineId: string;
  vollerName: string;
  tickets: HausmeisterTicket[];
  uebergaben: HausmeisterUebergabe[];
  bereitetVor: string | null;
  onAuftrag: (t: HausmeisterTicket) => void;
  onAlleAuftraege: () => void;
  onUebergabe: (u: HausmeisterUebergabe) => void;
  onFehler: (s: string | null) => void;
}) {
  const zeit = useZeit();
  const [timer, setTimer] = useState<TicketZeit | null>(null);
  const [werte, setWerte] = useState<Kennzahlen | null>(null);
  const [sek, setSek] = useState(() => new Date());
  const [stempelt, setStempelt] = useState(false);
  const grenzen = wochenGrenzen(zeit.woche);
  const von = grenzen?.von;
  const bis = grenzen?.bis;

  const laden = useCallback(async () => {
    if (!meineId) return;
    const [t, fahrten, material, abnahmen, notizen] = await Promise.all([
      laufenderTicketTimer(meineId),
      von && bis ? ladeFahrten({ mitarbeiterId: meineId, vonTag: von, bisTag: bis }) : Promise.resolve([]),
      ladeEigenesMaterial(meineId),
      zaehleEigeneAbnahmen(meineId),
      ladeLetzteNotizen(3),
    ]);
    setTimer(t);
    setWerte({
      km: fahrten.reduce((s, f) => s + Number(f.kilometer), 0),
      materialAnzahl: material.length,
      materialSumme: materialSumme(material),
      abnahmen,
      notizen,
    });
  }, [meineId, von, bis]);

  useEffect(() => { void laden(); }, [laden]);
  useEffect(() => {
    if (!timer) return;
    const i = setInterval(() => setSek(new Date()), 1000);
    return () => clearInterval(i);
  }, [timer]);

  const stempeln = async () => {
    if (stempelt) return;
    setStempelt(true);
    const ok = await (zeit.laufend ? zeit.beenden() : zeit.starten());
    setStempelt(false);
    if (!ok) onFehler('Die Arbeitszeit konnte nicht gespeichert werden. Bitte noch einmal versuchen.');
  };

  const jetzt = zeit.jetzt;
  const eingestempelt = !!zeit.laufend;
  const stufen = zaehleStufen(tickets);
  const laufendesTicket = timer ? tickets.find((t) => t.id === timer.ticket_id) ?? null : null;
  const naechste = alsNaechstes(tickets, timer?.ticket_id ?? null);
  const balken = wochenBalken(zeit.woche, tagVon(jetzt.toISOString()));
  const wocheIst = zeit.woche.reduce((s, w) => s + w.istMinuten, 0);
  const wocheSoll = zeit.woche.reduce((s, w) => s + w.sollMinuten, 0);
  const titelVon = (id: string) => tickets.find((t) => t.id === id)?.titel ?? 'Auftrag';
  const AzSymbol = eingestempelt ? MoonStars : SignIn;
  const azFarbe = eingestempelt ? 'gruen' : 'gold';

  return (
    <div className="p-rein" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '6px 4px 0' }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-w45)' }}>{datumLang(jetzt)}</span>
        <h1 style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: '-0.02em', overflowWrap: 'anywhere' }}>{gruss(jetzt, vollerName)}</h1>
      </div>

      <div className="hm-raster">
        {/* a: Arbeitszeit heute, Zähler, Jetzt dran */}
        <div style={{ gridArea: 'a', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <div className={`p-stapel ${farbKlasse(azFarbe)}`} style={{ paddingBottom: 12 }}>
            <div className={`p-kachel p-hebt ${farbKlasse(azFarbe)}`} style={{ borderRadius: 28, padding: '16px 16px 16px 18px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              <Timer aria-hidden size={130} weight="fill" style={{ position: 'absolute', right: 120, bottom: -40, opacity: 0.14, transform: 'rotate(-12deg)', zIndex: 0 }} />
              <div style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--p-w85)', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span className={eingestempelt ? 'p-puls' : ''} style={{ width: 7, height: 7, borderRadius: 4, background: 'var(--p-w)' }} />
                  Arbeitszeit heute
                </span>
                <span className="p-zahl" style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: 1.05 }}>{hm(zeit.heuteMinuten)} h</span>
                <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-w80)' }}>
                  {zeit.laufend ? `eingestempelt seit ${uhrzeit(zeit.laufend.beginn)} Uhr` : 'nicht eingestempelt'}
                </span>
              </div>
              <button
                type="button" onClick={() => void stempeln()} disabled={stempelt || !zeit.bereit}
                data-tip={eingestempelt ? 'Arbeitszeit beenden (ausstempeln)' : 'Arbeitszeit beginnen (einstempeln)'}
                className="p-knopf p-auf-farbe"
                style={{ height: 46, borderRadius: 23, padding: '0 18px', border: 'none', color: 'var(--p-w)', flexShrink: 0 }}
              >
                {stempelt ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <AzSymbol size={16} weight="fill" />}
                {eingestempelt ? 'Feierabend' : 'Kommen'}
              </button>
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 10 }}>
            {(['offen', 'in_bearbeitung', 'erledigt'] as const).map((k) => {
              const label = k === 'offen' ? 'Offen' : k === 'in_bearbeitung' ? 'In Arbeit' : 'Erledigt';
              return (
                <KennzahlKachel key={k} farbe={STUFE_STIL[k].farbe} icon={STUFE_STIL[k].icon} wert={String(stufen[k])} label={label}
                  tip={`${label} – Aufträge anzeigen`} onClick={onAlleAuftraege} />
              );
            })}
          </div>

          <GlasWidget farbe="timer" icon={Timer} titel="Jetzt dran" style={{ flex: 1 }}>
            {timer ? (
              <button
                type="button" className="p-zeile" disabled={!laufendesTicket}
                onClick={() => laufendesTicket && onAuftrag(laufendesTicket)}
                data-tip={laufendesTicket ? 'Laufenden Auftrag öffnen' : undefined}
                style={{ position: 'relative', gap: 12, padding: 12, borderRadius: 18, background: 'var(--p-w06)', boxShadow: 'inset 0 0 0 1px var(--p-w06)', cursor: laufendesTicket ? 'pointer' : 'default' }}
              >
                {laufendesTicket && <IconChip farbe={STUFE_STIL[statusStufe(laufendesTicket.status)].farbe} icon={STUFE_STIL[statusStufe(laufendesTicket.status)].icon} groesse={40} radius={12} />}
                <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <span className="p-kuerzen" style={{ fontSize: 14.5, fontWeight: 800 }}>{laufendesTicket?.titel ?? 'Zeit läuft auf einem Auftrag'}</span>
                  {laufendesTicket && <span className="p-kuerzen" style={{ fontSize: 12, fontWeight: 600, color: 'var(--p-w50)' }}>{adresse(laufendesTicket)}</span>}
                </span>
                <span style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2 }}>
                  <span className="p-zahl" style={{ fontSize: 22, fontWeight: 800, color: 'var(--p-warn)', letterSpacing: '-0.02em' }}>{hm(zeitMinuten(timer, sek))}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--p-w45)', display: 'flex', alignItems: 'center', gap: 5 }}>
                    <span className="p-puls" style={{ width: 6, height: 6, borderRadius: 3, background: 'var(--p-warn-punkt)' }} />läuft
                  </span>
                </span>
              </button>
            ) : (
              <span style={{ ...LEISE, padding: '14px 4px' }}>Keine Zeit gestartet. Öffne einen Auftrag und tippe „Zeit starten“.</span>
            )}
          </GlasWidget>
        </div>

        {/* b: Woche und Kennzahlen */}
        <div style={{ gridArea: 'b', display: 'flex', flexDirection: 'column', gap: 14, minWidth: 0 }}>
          <GlasWidget farbe="blau" icon={ChartBar} titel="Arbeitszeit diese Woche" style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span className="p-zahl" style={{ fontSize: 30, fontWeight: 800, letterSpacing: '-0.03em' }}>{hm(wocheIst)} h</span>
              {wocheSoll > 0 && <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--p-w50)' }}>von {hm(wocheSoll)} h Soll</span>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', gap: 8, alignItems: 'end', height: 96 }} role="img"
              aria-label={balken.map((b) => b.tip).join(', ')}>
              {balken.map((b) => (
                <div key={b.label} data-tip={b.tip} style={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: '100%', maxWidth: 26, height: b.hoehe, borderRadius: 8, background: b.heute ? 'var(--p-balken-heute)' : b.minuten ? 'var(--p-balken)' : 'var(--p-w08)', boxShadow: b.heute ? 'var(--p-balken-heute-sh)' : 'none' }} />
                  <span style={{ fontSize: 11, fontWeight: 800, color: b.heute ? 'var(--p-balken-heute-ink)' : 'var(--p-w45)' }}>{b.label}</span>
                </div>
              ))}
            </div>
          </GlasWidget>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 12 }}>
            <GlasKennzahl farbe="fahrt" icon={CarProfile} titel="Fahrten" wert={werte ? kmText(werte.km) : '–'} unter="diese Woche" tip="Eingetragene Kilometer über alle Aufträge" />
            <GlasKennzahl farbe="braun" icon={Package} titel="Material" wert={werte ? String(werte.materialAnzahl) : '–'} unter={werte ? euro(werte.materialSumme) : ''} tip="Selbst erfasste Material-Positionen" />
            <GlasKennzahl farbe="lila" icon={PenNib} titel="Abnahmen" wert={werte ? String(werte.abnahmen) : '–'} unter="unterschrieben" tip="Abnahmen mit Unterschrift vor Ort" />
          </div>
        </div>

        {/* u: Übergaben */}
        <GlasWidget farbe="schluessel" icon={Key} titel="Übergaben" style={{ gridArea: 'u' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {uebergaben.slice(0, 2).map((u) => {
              const st = uebergabeStatus(u);
              return (
                <button key={u.protokollId} type="button" className="p-zeile" data-tip="Übergabe öffnen" disabled={!!bereitetVor} onClick={() => onUebergabe(u)}>
                  {kalenderblatt(u.datum) ? (
                    <span aria-hidden style={{ width: 42, flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '4px 0', borderRadius: 11, background: 'var(--p-w08)' }}>
                      <span style={{ fontSize: 10, fontWeight: 800, color: 'var(--p-ton-info-fg)', textTransform: 'uppercase' }}>{kalenderblatt(u.datum)?.monat}</span>
                      <span style={{ fontSize: 17, fontWeight: 800, lineHeight: 1.1 }}>{kalenderblatt(u.datum)?.tag}</span>
                    </span>
                  ) : (
                    <IconChip farbe={ART_STIL[u.art].farbe} icon={ART_STIL[u.art].icon} groesse={42} radius={11} />
                  )}
                  <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <span className="p-kuerzen" style={{ fontSize: 13.5, fontWeight: 800 }}>{uebergabeTitel(u)}</span>
                    <span className="p-kuerzen" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--p-w50)' }}>
                      {[u.datum ? `${uhrzeit(u.datum)} Uhr` : '', u.adresse || u.objekt].filter(Boolean).join(' · ')}
                    </span>
                  </span>
                  {bereitetVor === u.protokollId ? <CircleNotch size={16} weight="bold" className="animate-spin" /> : <StatusChip ton={st.ton}>{st.text}</StatusChip>}
                </button>
              );
            })}
            {uebergaben.length === 0 && <span style={LEISE}>Keine Übergaben geplant.</span>}
          </div>
        </GlasWidget>

        {/* x: Als Nächstes */}
        <GlasWidget farbe="indigo" icon={ListChecks} titel="Als Nächstes" style={{ gridArea: 'x' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {naechste.map((t) => {
              const stil = STUFE_STIL[statusStufe(t.status)];
              return (
                <button key={t.id} type="button" className="p-zeile" data-tip="Auftrag öffnen" onClick={() => onAuftrag(t)}>
                  <IconChip farbe={stil.farbe} icon={stil.icon} groesse={32} radius={10} />
                  <span style={{ minWidth: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <span className="p-kuerzen" style={{ fontSize: 13.5, fontWeight: 800 }}>{t.titel}</span>
                    <span className="p-kuerzen" style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--p-w50)' }}>{adresse(t)}</span>
                  </span>
                  <CaretRight size={12} weight="bold" style={{ color: 'var(--p-w35)', flexShrink: 0 }} />
                </button>
              );
            })}
            {naechste.length === 0 && <span style={LEISE}>Alles erledigt.</span>}
          </div>
          <button
            type="button" onClick={onAlleAuftraege} data-tip="Alle freigegebenen Aufträge"
            style={{ alignSelf: 'flex-start', height: 34, padding: '0 14px', borderRadius: 17, border: 'none', cursor: 'pointer', fontSize: 12.5, fontWeight: 800, color: 'var(--p-lila-ink)', background: 'var(--p-lila-fl)', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            Alle Aufträge <ArrowRight size={12} weight="bold" />
          </button>
        </GlasWidget>

        {/* n: Letzte Notizen */}
        <GlasWidget farbe="gelb" icon={NotePencil} titel="Letzte Notizen" style={{ gridArea: 'n' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(werte?.notizen ?? []).map((n) => {
              const t = tickets.find((x) => x.id === n.ticket_id);
              return (
                <button
                  key={n.id} type="button" disabled={!t} onClick={() => t && onAuftrag(t)} data-tip={t ? 'Zum Auftrag' : undefined}
                  style={{ borderRadius: '16px 16px 16px 6px', padding: '10px 12px', border: 'none', cursor: t ? 'pointer' : 'default', textAlign: 'left', color: 'var(--p-w)', background: 'var(--p-w07)', display: 'flex', flexDirection: 'column', gap: 4 }}
                >
                  <span style={{ fontSize: 13.5, lineHeight: 1.45, color: 'var(--p-w88)', overflowWrap: 'anywhere' }}>{n.inhalt}</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--p-w40)' }}>{titelVon(n.ticket_id)} · {notizZeit(n.created_at)}</span>
                </button>
              );
            })}
            {werte && werte.notizen.length === 0 && <span style={LEISE}>Noch keine Notizen.</span>}
          </div>
        </GlasWidget>
      </div>
    </div>
  );
}
